if (!customElements.get('countdown-timer')) {
  customElements.define(
    'countdown-timer',
    class CountdownTimer extends HTMLElement {
      connectedCallback() {
        if (this.initialised || !this.isConnected) return;

        // Wait for the rest of the page (including this element's children and any "only while running" blocks).
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.connectedCallback(), { once: true });
          return;
        }

        this.wrapper = this.closest('.countdown__wrapper');

        if (!this.wrapper) {
          console.error('Countdown wrapper not found', this);
          return;
        }

        this.initialised = true;
        this.init();
      }

      disconnectedCallback() {
        clearInterval(this.intervalId);
        this.initialised = false;
        this.state = null;
      }

      init() {
        this.MS_IN_SEC = 1000;
        this.MS_IN_MIN = 60 * this.MS_IN_SEC;
        this.MS_IN_HRS = 60 * this.MS_IN_MIN;
        this.MS_IN_DAY = 24 * this.MS_IN_HRS;

        this.selectorMapping = {
          day: '[data-timer-day]',
          hour: '[data-timer-hour]',
          minute: '[data-timer-minute]',
          second: '[data-timer-second]',
        };
        this.units = ['day', 'hour', 'minute', 'second'];
        this.type = this.dataset.type;
        this.duration = this.dataset.duration;
        this.timezone = this.dataset.timezone;
        this.loop = false;
        this.shouldAddLeadingZero = true;
        // Show the timer before its start time in the theme editor, or with ?countdown_preview=1.
        this.previewing =
          this.hasAttribute('data-preview') || new URLSearchParams(window.location.search).has('countdown_preview');

        this.initCountdown();
      }

      initCountdown() {
        this.startTime = null;

        switch (this.type) {
          case 'fixed_time':
            this.targetTime = this.parseDate(this.dataset.dueDate);
            this.startTime = this.parseDate(this.dataset.startDate);
            break;
          default: // Evergreen.
            this.targetTime = this.getEvergreenDueDate().getTime();
            break;
        }

        if (!this.targetTime) {
          return;
        }

        this.fetchDomElements();
        this.tick();
        this.intervalId = setInterval(() => this.tick(), 1000);
      }

      fetchDomElements() {
        this.elements = {};
        this.units.forEach((unit) => {
          this.elements[unit] = this.querySelector(this.selectorMapping[unit]);
        });
        this.timerElement = this.querySelector('[data-timer]');
        this.endedElement = this.querySelector('[data-timer-ended]');
        this.runningOnlyElements = this.wrapper.querySelectorAll('[data-countdown-visibility="running"]');
      }

      tick() {
        const now = Date.now();
        let state = 'running';
        if (this.startTime && now < this.startTime && !this.previewing) {
          state = 'before';
        } else if (this.targetTime - now <= 0) {
          state = 'ended';
        }

        if (state === 'running') {
          this.updateDisplay(this.targetTime - now);
        } else if (state === 'ended') {
          if (this.loop) {
            this.initCountdown();
            return;
          }
          clearInterval(this.intervalId);
        }

        this.setState(state);
      }

      setState(state) {
        if (state === this.state) return;
        this.state = state;

        this.timerElement.hidden = state !== 'running';
        if (this.endedElement) this.endedElement.hidden = state !== 'ended';
        this.classList.toggle('hidden', state === 'before' || (state === 'ended' && !this.endedElement));
        this.runningOnlyElements.forEach((element) => {
          element.hidden = state !== 'running';
        });
      }

      updateDisplay(remainingTime) {
        const timeComponents = this.computeTimeComponents(remainingTime);
        this.units.forEach((unit) => {
          if (this.elements[unit]) {
            this.elements[unit].textContent = this.formatTimeComponent(timeComponents[unit]);
          }
        });
      }

      computeTimeComponents(ms) {
        return {
          day: Math.floor(ms / this.MS_IN_DAY),
          hour: Math.floor((ms % this.MS_IN_DAY) / this.MS_IN_HRS),
          minute: Math.floor((ms % this.MS_IN_HRS) / this.MS_IN_MIN),
          second: Math.floor((ms % this.MS_IN_MIN) / this.MS_IN_SEC),
        };
      }

      formatTimeComponent(value) {
        return this.shouldAddLeadingZero && value < 10 ? `0${value}` : value.toString();
      }

      // Parses "YYYY-MM-DD HH:MM" (also "YYYY/MM/DD hh:mm AM") as a wall-clock time in this.timezone.
      // A full ISO string with "Z" or an offset is used as-is.
      parseDate(value) {
        if (!value || !value.trim()) return null;
        value = value.trim();

        if (/(Z|[+-]\d{2}:?\d{2})$/i.test(value)) {
          const time = Date.parse(value);
          return isNaN(time) ? null : time;
        }

        const match = value.match(
          /^(\d{4})[-\/](\d{1,2})[-\/](\d{1,2})(?:[ T]+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([AaPp][Mm])?)?$/
        );
        if (!match) {
          console.error('Countdown timer: could not read the date', value);
          return null;
        }

        let [, year, month, day, hour = 0, minute = 0, second = 0, meridiem] = match;
        hour = Number(hour);
        if (meridiem) {
          hour = hour % 12;
          if (/p/i.test(meridiem)) hour += 12;
        }

        return this.zonedTimeToUtc(Number(year), Number(month), Number(day), hour, Number(minute), Number(second));
      }

      zonedTimeToUtc(year, month, day, hour, minute, second) {
        const wallClock = Date.UTC(year, month - 1, day, hour, minute, second);
        if (!this.timezone) return wallClock;

        try {
          // Correct twice so times either side of a daylight saving change land on the right offset.
          let utc = wallClock - this.getTimezoneOffset(wallClock);
          utc = wallClock - this.getTimezoneOffset(utc);
          return utc;
        } catch (error) {
          console.error('Countdown timer: unknown time zone', this.timezone);
          return wallClock;
        }
      }

      // Milliseconds this.timezone is ahead of UTC at the given moment.
      getTimezoneOffset(time) {
        this.timezoneFormat =
          this.timezoneFormat ||
          new Intl.DateTimeFormat('en-GB', {
            timeZone: this.timezone,
            hourCycle: 'h23',
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
          });
        const parts = {};
        this.timezoneFormat.formatToParts(new Date(time)).forEach(({ type, value }) => {
          parts[type] = Number(value);
        });
        const zoned = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
        return zoned - Math.floor(time / 1000) * 1000;
      }

      getEvergreenDueDate() {
        let intervalHrs;
        const now = new Date();
        const dueDate = new Date(now);

        switch (this.duration) {
          case 'every_month':
            dueDate.setMonth(dueDate.getMonth() + 1); // Next month.
            dueDate.setDate(1); // Start from the 1st day of the next month.
            dueDate.setHours(0, 0, 0, 0);
            return dueDate;
          case 'every_week':
            const dayOfWeek = now.getDay();
            const daysUntilNextWeek = 7 - dayOfWeek;
            dueDate.setDate(now.getDate() + daysUntilNextWeek); // Move to next Sunday.
            dueDate.setHours(0, 0, 0, 0); // Set the time to 00:00:00.
            return dueDate;
          case 'every_2_hrs':
            intervalHrs = 2;
            break;
          case 'every_6_hrs':
            intervalHrs = 6;
            break;
          case 'every_12_hrs':
            intervalHrs = 12;
            break;
          case 'every_24_hrs':
            intervalHrs = 24;
            break;
        }

        const currentHour = now.getHours();

        // Possible start points based on the interval.
        const startPoints = [];
        for (let i = 0; i < 24; i += intervalHrs) {
          startPoints.push(i);
        }

        // Find the next start point.
        for (let i = 0; i < startPoints.length; i++) {
          if (
            currentHour < startPoints[i] ||
            (currentHour === startPoints[i] && now.getMinutes() === 0 && now.getSeconds() === 0)
          ) {
            dueDate.setHours(startPoints[i], 0, 0, 0);
            return dueDate;
          }
        }

        // If no future point today, roll over to the first point tomorrow.
        dueDate.setDate(dueDate.getDate() + 1);
        dueDate.setHours(startPoints[0], 0, 0, 0);
        return dueDate;
      }
    }
  );
}
